/**
 * Сводный отчёт по нарядам — «Наряды зубного техника».
 *
 * Ключевое свойство сохранённого отчёта: `rows` — это ПЛОСКИЙ СНИМОК на момент
 * сохранения, а не ссылки на заказы (`02-DATA-MODEL.md`). Имя врача, название
 * конструкции и цена копируются текстом. Если завтра заказ отредактируют или
 * врача переименуют, ранее отданный клинике отчёт обязан остаться прежним —
 * иначе распечатка на столе и отчёт в системе перестанут совпадать.
 *
 * Поэтому здесь нет ни одного обращения к справочникам: на вход подаются уже
 * снятые значения.
 *
 * Два вида наряда:
 *   - обычный — одна строка на заказ, работа одной фразой;
 *   - РАЗВЁРНУТЫЙ — работа каждого заказа расписана по элементам с ценами
 *     (аппарат, печать модели, крючки), под ними «Итого по заказу». Так
 *     принимает наряды часть клиник; прежде его писали от руки на каждый заказ.
 */
import { formatDate, formatDateDots } from '../dates.js';
import { formatKop } from '../money.js';
import { itemsTotalKop } from '../order-breakdown.js';
/**
 * Дата в наряде: `01.04.2026 г.`
 *
 * Формат взят из наряда лаборатории («Наряд СЦ Апрель 2026»): точки и «г.»
 * в конце. Тот же вид, что у даты договора в акте, — в бумагах лаборатории
 * это единый способ записи календарной ссылки.
 */
export function reportDate(date) {
    const dots = formatDateDots(date);
    return dots ? `${dots} г.` : '';
}
/**
 * Приведение строк к виду наряда.
 *
 * В ОБЫЧНОМ наряде элементы отбрасываются: им там нет места, а храниться
 * молча они не должны — иначе сохранённый обычный отчёт при следующем
 * открытии распознался бы как развёрнутый.
 *
 * В РАЗВЁРНУТОМ у каждой строки обязательно есть элементы, а сумма строки
 * пересчитывается по ним. Строка без элементов получает один — саму работу
 * целиком, — чтобы расшифровка не оказалась пустой. Пересчёт по элементам —
 * единственный источник суммы: после ручной правки цены элемента итог заказа
 * и общий итог сходятся сами, без второй правки.
 */
export function normalizeReportRows(rows, detailed) {
    return rows.map((row) => {
        if (!detailed) {
            const plain = { ...row };
            delete plain.items;
            return plain;
        }
        const items = row.items && row.items.length > 0
            ? row.items
            : [{ name: row.construction, qty: 1, priceKop: row.costKop }];
        return { ...row, items, costKop: itemsTotalKop(items) };
    });
}
/** Строки без фамилии — в конец: «—» и «Врач не указан» не должны открывать наряд. */
const безИмени = (name) => !name || name === '—' || name === 'Врач не указан';
/** Сравнение фамилий для наряда: по алфавиту, безымянные — в конце. */
export function compareReportNames(a, b) {
    if (безИмени(a) !== безИмени(b))
        return безИмени(a) ? 1 : -1;
    return a.localeCompare(b, 'ru', { sensitivity: 'base' });
}
/**
 * Порядок строк наряда: врачи по алфавиту, внутри врача — пациенты по алфавиту.
 *
 * Прежде строки шли в порядке, в каком заказы стояли в журнале: предпросмотр
 * в «Заказах» сортировал врачей сам, а сохранённый наряд, PDF и Excel — нет,
 * и в готовом отчёте фамилии шли вразнобой. Сортировка здесь — в одном месте
 * для экрана, печати, PDF и Excel.
 */
export function sortReportRows(rows) {
    return [...rows].sort((a, b) => compareReportNames(a.doctor || '—', b.doctor || '—') || compareReportNames(a.patient, b.patient));
}
/** Группировка строк по врачам — в порядке `sortReportRows`. */
export function groupByDoctor(rows) {
    const groups = new Map();
    for (const row of sortReportRows(rows)) {
        const key = row.doctor || '—';
        let group = groups.get(key);
        if (!group) {
            group = { doctorName: key, rows: [], subtotalKop: 0 };
            groups.set(key, group);
        }
        group.rows.push(row);
        group.subtotalKop += row.costKop;
    }
    return [...groups.values()];
}
/** Заголовки колонок. Колонки дат — опциональные, их набор выбирает пользователь. */
export function reportHeaders(showReceived, showDue, detailed = false) {
    /*
     * В развёрнутом наряде вместо одной «Конструкции» — элемент, количество и
     * цена за штуку, а последняя колонка — сумма: по ней считаются итоги.
     */
    const headers = detailed
        ? ['ФИО врача', 'ФИО пациента', 'Элемент', 'Кол-во', 'Цена']
        : ['ФИО врача', 'ФИО пациента', 'Конструкция'];
    // Порядок и названия — из наряда лаборатории. «Дата сдачи» стоит перед
    // ценой: по ней техник и сверяет наряд с тем, что уже отдано.
    if (showReceived)
        headers.push('Получен');
    if (showDue)
        headers.push('Дата сдачи');
    headers.push(detailed ? 'Сумма' : 'Цена');
    return headers;
}
export function buildReport(input) {
    const detailed = input.detailed ?? input.rows.some((row) => (row.items?.length ?? 0) > 0);
    const rows = normalizeReportRows(input.rows, detailed);
    const groups = groupByDoctor(rows);
    const totalKop = input.totalKop ?? rows.reduce((sum, row) => sum + row.costKop, 0);
    return {
        clinicName: input.clinicName,
        legalName: input.legalName,
        periodLabel: input.periodLabel,
        responsible: input.responsible,
        showReceived: input.showReceived,
        showDue: input.showDue,
        showSubtotal: input.showSubtotal,
        detailed,
        headers: reportHeaders(input.showReceived, input.showDue, detailed),
        groups,
        rowCount: rows.length,
        totalKop,
        total: formatKop(totalKop),
        date: formatDate(input.date),
        // Дата шапки — в том же виде, что и даты в строках наряда.
        dateLabel: reportDate(input.date),
        facsimile: input.facsimile ? (input.facsimileImages ?? null) : null,
    };
}
/** Значения одной строки таблицы в порядке колонок. */
export function reportRowValues(model, doctorName, row) {
    const values = [doctorName, row.patient, row.construction];
    if (model.showReceived)
        values.push(reportDate(row.received));
    if (model.showDue)
        values.push(reportDate(row.due));
    // Цена — числом, а не строкой: в Excel по ней должны работать формулы и
    // автосумма, а текст «3 800» пользователь просуммировать не сможет.
    values.push(row.costKop / 100);
    return values;
}
/** Имя файла отчёта — по клинике и периоду, а не по идентификатору. */
export function reportFileName(model, extension) {
    const safeClinic = model.clinicName.replace(/[\\/:*?"<>|]/g, '-');
    return `Наряды ${safeClinic} ${model.periodLabel}.${extension}`;
}
//# sourceMappingURL=report.js.map